import {
  captureCueboardObservabilityEvent,
  sanitizeTelemetryRoute,
} from '@/utils/posthog';

import { HTTPStatusError } from './chat-api';

export type DiscordRuntimeStatus = {
  platform: string;
  configured: boolean;
  online: boolean;
  config_error?: string;
  recent_activity_at?: number | null;
  recent_error?: string;
};

export type DiscordInstallMetadata = {
  invite_url?: string | null;
  install_url?: string | null;
  authorization_url?: string | null;
  client_id?: string | null;
  application_id?: string | null;
  application_name?: string | null;
  bot_username?: string | null;
  bot_name?: string | null;
  bot_user_id?: string | null;
  scopes?: string[];
  permissions?: string | number | null;
};

export type DiscordJoinedGuildSummary = {
  guild_id: string;
  guild_name?: string;
  icon_url?: string;
  ownership_state?: string;
  install_status?: string;
  shared_agent_id?: string | null;
  recent_error?: string | null;
  recent_activity_at?: string | null;
  joined_at?: string | null;
};

export type DiscordGuildConfig = {
  id: string;
  owner_user_id: string;
  ownership_state?: string;
  guild_id: string;
  guild_name: string;
  allowed_role_ids: string[];
  install_status: string;
  shared_agent_id?: string;
  recent_error?: string;
  recent_activity_at?: string | null;
  joined_at?: string | null;
  created_at: string;
  updated_at: string;
};

export type DiscordDMClaimCode = {
  code: string;
  expires_at: string;
};

export type DiscordGuildVerificationCode = {
  code: string;
  expires_at: string;
};

export type DiscordDMLink = {
  discord_user_id: string;
  discord_username?: string;
  created_at: string;
  updated_at: string;
};

export type DiscordGuildConfigList = {
  guilds: DiscordGuildConfig[];
  joined_guilds?: DiscordJoinedGuildSummary[] | null;
  install_metadata?: DiscordInstallMetadata | null;
  runtime?: DiscordRuntimeStatus | null;
  dm_link?: DiscordDMLink | null;
  dm_claim_code?: DiscordDMClaimCode | null;
  official_login_enabled?: boolean;
};

export type DiscordGuildRole = {
  id: string;
  role_id?: string;
  name: string;
  managed?: boolean;
  color?: number;
  hoist?: boolean;
  position?: number;
  mentionable?: boolean;
};

export type DiscordGuildRolesList = {
  guild_id?: string;
  guild_name?: string;
  roles: DiscordGuildRole[];
};

export type DiscordConnectStartResponse = {
  authorization_url: string;
};

export type DiscordConnectCompleteResponse = {
  message?: string;
  discord_user_id?: string;
  discord_username?: string;
  redirect_url?: string;
};

type RequestInitWithBody = RequestInit & {
  body?: BodyInit | null;
};

function buildErrorMessage(status: number, body: unknown): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'object' &&
    body.error !== null &&
    'message' in body.error &&
    typeof body.error.message === 'string'
  ) {
    return body.error.message;
  }

  if (typeof body === 'string' && body.trim()) {
    return body.trim();
  }

  return `Request failed (${status})`;
}

async function readErrorBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    return response.json().catch(() => null);
  }
  return response.text().catch(() => null);
}

async function fetchDiscordJSON<T>(
  path: string,
  init?: RequestInitWithBody
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'include',
      ...init,
    });
  } catch (error) {
    captureDiscordFailure(path, init?.method, undefined, error);
    throw error;
  }

  if (!response.ok) {
    const body = await readErrorBody(response);
    const message = buildErrorMessage(response.status, body);
    if (response.status >= 500) {
      captureDiscordFailure(
        path,
        init?.method,
        response.status,
        new Error(message)
      );
    }
    throw new HTTPStatusError(response.status, message);
  }

  return (await response.json()) as T;
}

function captureDiscordFailure(
  path: string,
  method: string | undefined,
  statusCode: number | undefined,
  error: unknown
) {
  const routeClass = sanitizeTelemetryRoute(path);
  captureCueboardObservabilityEvent({
    failureClass: 'integration.discord_failed',
    severity: 'error',
    surface: 'website_integration',
    platform: 'web',
    entrySurface: 'cueboard_web_app',
    integrationProvider: 'discord',
    integrationSurface: path.includes('/connect/')
      ? 'oauth'
      : path.includes('/guild')
        ? 'guild'
        : 'dm',
    statusCode,
    networkErrorKind: statusCode == null ? 'transport_error' : undefined,
    diagnosisHint:
      'Use request_id if present and the matching backend route_class to inspect Discord integration logs.',
    error,
    properties: {
      method: method || 'GET',
      route_class: routeClass,
      suspect_layer: statusCode == null ? 'client_network' : 'integration',
    },
  });
}

export async function listDiscordGuildConfigs(): Promise<DiscordGuildConfigList> {
  return fetchDiscordJSON<DiscordGuildConfigList>('/v1/user/discord/guilds', {
    method: 'GET',
  });
}

export async function listDiscordGuildRoles(
  guildID: string
): Promise<DiscordGuildRolesList> {
  const response = await fetchDiscordJSON<unknown>(
    `/v1/user/discord/guilds/${encodeURIComponent(guildID)}/roles`,
    {
      method: 'GET',
    }
  );

  if (Array.isArray(response)) {
    return {
      roles: response.map(value => {
        if (typeof value !== 'object' || value === null) {
          return { id: '', name: '' } as DiscordGuildRole;
        }
        const role = value as Record<string, unknown>;
        const roleID =
          typeof role.role_id === 'string'
            ? role.role_id
            : typeof role.id === 'string'
              ? role.id
              : '';
        return {
          ...(role as DiscordGuildRole),
          id: roleID,
          role_id: roleID,
          name: typeof role.name === 'string' ? role.name : '',
        };
      }),
    };
  }

  if (typeof response === 'object' && response !== null) {
    const value = response as Record<string, unknown>;
    const roles = value.roles;
    if (Array.isArray(roles)) {
      return {
        guild_id:
          typeof value.guild_id === 'string' ? value.guild_id : undefined,
        guild_name:
          typeof value.guild_name === 'string' ? value.guild_name : undefined,
        roles: roles as DiscordGuildRole[],
      };
    }
  }

  return { roles: [] };
}

export async function upsertDiscordGuildConfig(
  guildID: string,
  params: {
    guildName: string;
    allowedRoleIDs: string[];
  }
): Promise<DiscordGuildConfig> {
  return fetchDiscordJSON<DiscordGuildConfig>(
    `/v1/user/discord/guilds/${encodeURIComponent(guildID)}`,
    {
      method: 'PUT',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        guild_name: params.guildName,
        allowed_role_ids: params.allowedRoleIDs,
      }),
    }
  );
}

export async function createDiscordDMClaimCode(): Promise<DiscordDMClaimCode> {
  return fetchDiscordJSON<DiscordDMClaimCode>('/v1/user/discord/dm-claim', {
    method: 'POST',
  });
}

export async function startDiscordConnect(params?: {
  dmHandoffToken?: string;
}): Promise<DiscordConnectStartResponse> {
  return fetchDiscordJSON<DiscordConnectStartResponse>(
    '/v1/user/discord/connect/start',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        dm_handoff_token: params?.dmHandoffToken ?? '',
      }),
    }
  );
}

export async function completeDiscordConnect(
  code: string,
  state: string
): Promise<DiscordConnectCompleteResponse> {
  return fetchDiscordJSON<DiscordConnectCompleteResponse>(
    '/v1/integrations/discord/connect/complete',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ code, state }),
    }
  );
}

export async function createDiscordGuildVerificationCode(): Promise<DiscordGuildVerificationCode> {
  return fetchDiscordJSON<DiscordGuildVerificationCode>(
    '/v1/user/discord/guild-verification-code',
    {
      method: 'POST',
    }
  );
}

export async function deleteDiscordDMLink(): Promise<void> {
  const path = '/v1/user/discord/dm-link';
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'include',
      method: 'DELETE',
    });
  } catch (error) {
    captureDiscordFailure(path, 'DELETE', undefined, error);
    throw error;
  }

  if (!response.ok) {
    const body = await readErrorBody(response);
    const message = buildErrorMessage(response.status, body);
    if (response.status >= 500) {
      captureDiscordFailure(
        path,
        'DELETE',
        response.status,
        new Error(message)
      );
    }
    throw new HTTPStatusError(response.status, message);
  }
}

export function buildDiscordInstallURL(
  metadata?: DiscordInstallMetadata | null
): string | null {
  if (!metadata) {
    return null;
  }

  const directURL =
    metadata.invite_url?.trim() ||
    metadata.install_url?.trim() ||
    metadata.authorization_url?.trim();
  if (directURL) {
    return directURL;
  }

  const clientID = metadata.client_id?.trim();
  const applicationID = metadata.application_id?.trim();
  const resolvedClientID = clientID || applicationID;
  if (!resolvedClientID) {
    return null;
  }

  const params = new URLSearchParams({
    client_id: resolvedClientID,
    scope: 'bot',
  });

  const permissions =
    typeof metadata.permissions === 'number'
      ? String(metadata.permissions)
      : metadata.permissions?.trim();
  if (permissions) {
    params.set('permissions', permissions);
  }

  return `https://discord.com/oauth2/authorize?${params.toString()}`;
}

export const resolveDiscordInstallURL = buildDiscordInstallURL;
